import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export type Language = "uk" | "en";

const messages = {
  uk: {
    home: "Головна", catalog: "Каталог", customOrder: "Індивідуальне замовлення",
    chooseInstrument: "Обрати інструмент", openMenu: "Відкрити меню", navigation: "Основна навігація",
    lightTheme: "Світла тема", darkTheme: "Темна тема", language: "Мова",
    heroEyebrow: "Музичні інструменти з характером", heroTitle: "Знайдіть інструмент, який звучить як ви.",
    heroLead: "Допоможемо обрати готову модель або створимо унікальний інструмент разом із майстром.",
    viewCatalog: "Переглянути каталог", createOwn: "Створити свій →", yearsExperience: "років досвіду",
    instruments: "інструментів", returnDays: "на повернення", musicianAlt: "Музикант із гітарою",
    weeklyChoice: "Вибір тижня", liveSound: "Живий звук без компромісів", startSound: "Почніть зі свого звучання",
    fullCatalog: "Увесь каталог →", workshop: "Майстерня muzshop",
    uniqueTitle: "Такого інструмента більше ні в кого не буде",
    uniqueLead: "Оберіть форму, матеріал, колір і деталі. Ми оцінимо ідею, погодимо ескіз і показуватимемо кожен етап створення.",
    openBuilder: "Відкрити конфігуратор", guitars: "Гітари", violins: "Скрипки", drums: "Ударні", winds: "Духові",
    guitarsCaption: "Від класики до сцени", violinsCaption: "Теплий живий звук", drumsCaption: "Ритм без компромісів", windsCaption: "Характер у кожній ноті",
    catalogTitle: "Інструменти для вашої музики", search: "Пошук за назвою", defaultSort: "За замовчуванням",
    cheapFirst: "Спочатку дешевші", expensiveFirst: "Спочатку дорожчі", cart: "Кошик", all: "Усі", saxophones: "Саксофони",
    inStock: "У наявності", checked: "Перевірений і налаштований перед відправленням", detailsAbout: "Докладніше про",
    addToCart: "Додати до кошика", remove: "Прибрати", yourCart: "Ваш кошик", emptyCart: "Тут поки що порожньо",
    checkout: "Оформити замовлення", manufacturer: "Виробник", category: "Категорія", feature: "Особливість",
    warranty: "Гарантія", months12: "12 місяців", price: "Ціна", freeSetup: "Безкоштовне налаштування перед відправленням · 14 днів на повернення",
    close: "Закрити", leaveContacts: "Залиште контакти", formLead: "Уточнимо деталі, терміни й точну вартість. Без спаму та нав’язливих дзвінків.",
    yourName: "Ваше ім’я", namePlaceholder: "Як до вас звертатися", phone: "Телефон", optional: "необов’язково",
    sendRequest: "Надіслати заявку", sending: "Надсилаємо…", requestAccepted: "Заявку прийнято",
    contactSoon: "Зв’яжемося з вами протягом робочого дня.", done: "Готово",
    footerTagline: "Інструменти, на яких хочеться грати.", contact: "Зв’язатися", delivery: "Київ · Доставка по Україні",
  },
  en: {
    home: "Home", catalog: "Catalog", customOrder: "Custom order",
    chooseInstrument: "Choose an instrument", openMenu: "Open menu", navigation: "Main navigation",
    lightTheme: "Light theme", darkTheme: "Dark theme", language: "Language",
    heroEyebrow: "Musical instruments with character", heroTitle: "Find the instrument that sounds like you.",
    heroLead: "We’ll help you choose a ready-made model or create a unique instrument together with a craftsperson.",
    viewCatalog: "Browse catalog", createOwn: "Create your own →", yearsExperience: "years of experience",
    instruments: "instruments", returnDays: "returns", musicianAlt: "Musician with a guitar",
    weeklyChoice: "Pick of the week", liveSound: "Authentic sound without compromise", startSound: "Start with your sound",
    fullCatalog: "Full catalog →", workshop: "muzshop workshop",
    uniqueTitle: "An instrument no one else will have",
    uniqueLead: "Choose the shape, material, color, and details. We’ll assess the idea, approve the sketch, and show you every stage of the build.",
    openBuilder: "Open configurator", guitars: "Guitars", violins: "Violins", drums: "Drums", winds: "Winds",
    guitarsCaption: "From classical to stage", violinsCaption: "Warm, vibrant sound", drumsCaption: "Rhythm without compromise", windsCaption: "Character in every note",
    catalogTitle: "Instruments for your music", search: "Search by name", defaultSort: "Default",
    cheapFirst: "Price: low to high", expensiveFirst: "Price: high to low", cart: "Cart", all: "All", saxophones: "Saxophones",
    inStock: "In stock", checked: "Inspected and set up before shipping", detailsAbout: "Details about",
    addToCart: "Add to cart", remove: "Remove", yourCart: "Your cart", emptyCart: "Your cart is empty",
    checkout: "Checkout", manufacturer: "Manufacturer", category: "Category", feature: "Feature",
    warranty: "Warranty", months12: "12 months", price: "Price", freeSetup: "Free setup before shipping · 14-day returns",
    close: "Close", leaveContacts: "Leave your contact details", formLead: "We’ll confirm the details, timing, and final price. No spam or pushy calls.",
    yourName: "Your name", namePlaceholder: "How should we address you?", phone: "Phone", optional: "optional",
    sendRequest: "Send request", sending: "Sending…", requestAccepted: "Request received",
    contactSoon: "We’ll contact you within one business day.", done: "Done",
    footerTagline: "Instruments you’ll want to play.", contact: "Contact", delivery: "Kyiv · Delivery across Ukraine",
  },
} as const;

type MessageKey = keyof typeof messages.uk;
interface I18nValue { language: Language; setLanguage: (language: Language) => void; t: (key: MessageKey) => string; }
const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("muzshop-language");
    if (saved === "uk" || saved === "en") return saved;
    const browserLanguage = navigator.languages?.[0] || navigator.language;
    return browserLanguage.toLowerCase().startsWith("en") ? "en" : "uk";
  });
  useEffect(() => {
    localStorage.setItem("muzshop-language", language);
    document.documentElement.lang = language;
  }, [language]);
  const value = useMemo(() => ({ language, setLanguage, t: (key: MessageKey) => messages[language][key] }), [language]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}

const englishProductNames: Record<string, string> = {
  "1": "Yamaha C40 Guitar", "2": "Fender Squier Electric Guitar", "3": "Yamaha V5 Violin",
  "4": "Cervini HV-100 Violin", "5": "Yamaha YAS-280 Saxophone", "6": "Yamaha Rydeen Drum Kit",
  "7": "Ibanez GRX40 Electric Guitar", "8": "Cort AD810 Acoustic Guitar", "9": "Gibson Les Paul Studio Electric Guitar",
  "10": "Cordoba C5 Classical Guitar", "11": "Stentor Student II Violin", "12": "Gewa Allegro Violin",
  "13": "Cremona SV-175 Violin", "14": "Selmer Axos Saxophone", "15": "Jupiter JAS700 Saxophone",
  "16": "Roy Benson AS-202 Saxophone", "17": "Pearl Roadshow Drum Kit", "18": "Tama Imperialstar Drum Kit",
  "19": "Mapex Tornado Drum Kit", "20": "Roland TD-07 Electronic Drums",
};
export const localizeProductTitle = (id: string, fallback: string, language: Language) =>
  language === "en" ? englishProductNames[id] ?? fallback : fallback;
